import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Undo2 } from 'lucide-react';
import { formatDateTime, getRoleLabel } from '../../lib/utils';

export function ApprovalsTab({
  request,
  approvals,
  isAdminLevel,
  isActiveCoordinator = false,
  currentUserId,
  handleUndoApproval,
}) {
  // Undo: admin-level users (Admin / Chairman / CRD), not after the request is finalized.
  // iter126 — the active CTA Coordinator can ALSO undo:
  //   (a) their own COORDINATOR approval, and
  //   (b) PI / INVESTIGATOR / REVIEWER approvals (the people they assigned).
  // The backend enforces the same matrix in DELETE /requests/{id}/approvals/{approval_id}.
  const finalized = ['APPROVED', 'FAILED', 'EXECUTED'].includes(request.status);
  const adminCanShow = isAdminLevel && !finalized;
  const coordinatorCanShow = isActiveCoordinator && !finalized;
  const canShowActions = adminCanShow || coordinatorCanShow;

  const FT_UNDOABLE_ROLE_BY_STATUS = {
    FT_PENDING_CHAIRMAN_FINAL: 'FINANCIAL_OFFICER',
    FT_PENDING_FINANCE: 'CHAIRMAN',
    FT_PENDING_DIRECTOR: 'FINANCE',
    FT_PENDING_EXEC_DIR_REVIEW: 'DIRECTOR',
    PENDING_SIGNATURES: 'EXEC_DIRECTOR',
  };

  const isUndoableByStage = (approval) =>
    request.type !== 'FT' || FT_UNDOABLE_ROLE_BY_STATUS[request.status] === approval.user_role;

  // Per-row authorization. Admin always wins. The Coordinator path is scoped to the
  // approval roles they are allowed to undo.
  const canUndoRow = (approval) => {
    if (!isUndoableByStage(approval)) return false;
    if (adminCanShow) return true;
    if (coordinatorCanShow) {
      const isOwn = approval.user_id === currentUserId;
      const isReviewerOrPI = ['PI', 'INVESTIGATOR', 'REVIEWER'].includes(approval.user_role);
      return isOwn || isReviewerOrPI;
    }
    return false;
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'APPROVE': return 'Approved';
      case 'MARK_FAILED': return 'Marked Failed';
      case 'FINAL_APPROVE': return 'Final Approval';
      case 'FINAL_FAIL': return 'Confirmed Failed';
      default: return action;
    }
  };

  const getActionBadgeClass = (action) => {
    if (action === 'APPROVE' || action === 'FINAL_APPROVE') {
      return 'bg-emerald-100 text-emerald-700';
    }
    return 'bg-red-100 text-red-700';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Approvals</CardTitle>
      </CardHeader>
      <CardContent>
        {approvals.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No approvals yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Action</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                  {canShowActions && (
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {approvals.map((approval) => (
                  <tr key={approval.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm">{approval.user_name}</td>
                    <td className="py-3 px-4 text-sm">
                      <Badge variant="outline">{getRoleLabel(approval.user_role)}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <Badge className={getActionBadgeClass(approval.action)}>
                        {getActionLabel(approval.action)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <Badge 
                        variant="outline" 
                        className={approval.status === 'COMPLETED' ? 'text-emerald-600' : 'text-amber-600'}
                      >
                        {approval.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {formatDateTime(approval.created_at)}
                    </td>
                    {canShowActions && (
                      <td className="py-3 px-4 text-sm">
                        {canUndoRow(approval) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleUndoApproval(approval.id)}
                            className="text-amber-600 hover:text-amber-700"
                            data-testid={`undo-approval-${approval.id}`}
                          >
                            <Undo2 size={14} className="mr-1" />
                            Undo
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ApprovalsTab;
